import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import styles from './styles.module.less';

/*
EnsureInsideContainer takes a percentageLeft as a starting point, but will check
the position and adjust to make sure the rendered children fit inside the parentRef container,
with a horizontalPadding on either side.
*/

const EnsureInsideContainer = props => {
  const { children, horizontalPadding, parentRef, percentageLeft } = props;

  const ref = useRef(null);
  const [isRightOverflowed, setRightOverflowed] = useState(false);

  const adjustPositionInContainer = useCallback(() => {
    if (!parentRef) {
      return;
    }

    const parentRect = parentRef.getBoundingClientRect();
    const targetPosition =
      (parseFloat(percentageLeft) / 100) * parentRect.width;

    const rect = ref.current.getBoundingClientRect();
    const rightOverflow = rect.right > parentRect.right - horizontalPadding;
    const leftOverflow = rect.left < parentRect.left + horizontalPadding;
    const isRightOfTargetPosition = ref.current.offsetLeft > targetPosition;

    const positionedElementStyle = ref.current.style;

    if (rightOverflow) {
      setRightOverflowed(true);
      positionedElementStyle.left = null;
      positionedElementStyle.right = `${horizontalPadding}px`;
    } else if (leftOverflow) {
      positionedElementStyle.left = `${horizontalPadding}px`;
      positionedElementStyle.right = null;
    } else if (isRightOfTargetPosition && isRightOverflowed) {
      // no overflow + we are past the desired position -->
      // we are stuck to the right side and need to reset
      setRightOverflowed(false);
      positionedElementStyle.left = percentageLeft;
      positionedElementStyle.right = null;
    }
  }, [horizontalPadding, parentRef, percentageLeft, isRightOverflowed]);

  useLayoutEffect(() => {
    adjustPositionInContainer();
  }, [adjustPositionInContainer]);

  useEffect(() => {
    window.addEventListener('resize', adjustPositionInContainer);

    return () =>
      window.removeEventListener('resize', adjustPositionInContainer);
  }, [adjustPositionInContainer]);

  return (
    <div
      ref={ref}
      className={styles.positionedElement}
      style={{ left: percentageLeft }}
    >
      {children}
    </div>
  );
};

export default EnsureInsideContainer;
